import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { MARKETPLACE_ORDER_ROLLUP_STATUSES, type MarketplaceOrderRollupStatus, type PlatformMarketplaceOrderSummary } from "../../api/types";

function statusBadgeClass(status: MarketplaceOrderRollupStatus): string {
  switch (status) {
    case "DELIVERED":
      return "badge badge--active";
    case "FAILED":
      return "badge badge--suspended";
    case "PENDING":
      return "badge badge--fresh";
    default:
      return "badge badge--demo";
  }
}

/**
 * §16's platform-driven fulfillment list: every marketplace order across every tenant,
 * with a LIVE-recomputed rollup status (`compute_rollup_status` — never a stored value,
 * see `PlatformMarketplaceOrderSummary.status`'s doc comment in `api/types.ts`). Filtering
 * by status happens server-side (`GET /platform/marketplace/orders?status=...`), same as
 * the backend's own post-rollup Python filter.
 */
export function MarketplaceOrdersPage() {
  const client = usePlatformClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<MarketplaceOrderRollupStatus | "">("");

  const fetchOrders = useCallback(
    () => client.listMarketplaceOrders(statusFilter ? { status: statusFilter } : undefined),
    [client, statusFilter],
  );
  const { data, loading, error } = useAsyncData(fetchOrders, [fetchOrders]);

  const columns: DataTableColumn<PlatformMarketplaceOrderSummary, string>[] = [
    {
      key: "id",
      header: "Order",
      render: (row) => (
        <button
          type="button"
          className="sl-data-table__sort-button"
          style={{ fontWeight: 600 }}
          onClick={() => navigate(`/marketplace/orders/${row.id}`)}
        >
          {row.id.slice(0, 8)}…
        </button>
      ),
    },
    {
      key: "contact",
      header: "Contact",
      render: (row) => (
        <>
          <div>{row.contact_name}</div>
          <div className="muted">{row.contact_email}</div>
        </>
      ),
    },
    {
      key: "grand_total",
      header: "Total",
      align: "right",
      render: (row) => `${row.grand_total} ${row.currency ?? ""}`.trim(),
    },
    { key: "tenant_count", header: "Sellers", align: "right", render: (row) => row.tenant_count },
    {
      key: "status",
      header: "Status",
      render: (row) => <span className={statusBadgeClass(row.status)}>{row.status}</span>,
    },
    { key: "created_at", header: "Placed", render: (row) => new Date(row.created_at).toLocaleString() },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Marketplace Orders</h1>
          <p className="page__subtitle">
            Every marketplace order across every business, split by seller. Status is recomputed live from each
            suborder's real shipment state, never a stored value.
          </p>
        </div>
      </div>

      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}

      <div className="filter-bar">
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="marketplace-status-filter">
            Status
          </label>
          <select
            id="marketplace-status-filter"
            className="sl-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as MarketplaceOrderRollupStatus | "")}
          >
            <option value="">All</option>
            {MARKETPLACE_ORDER_ROLLUP_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="panel">
        <DataTable
          columns={columns}
          rows={data ?? []}
          getRowId={(row) => row.id}
          emptyMessage={loading ? "Loading…" : "No marketplace orders."}
        />
      </div>
    </div>
  );
}
